import { initializeApp } from 'firebase/app';
import {
  createUserWithEmailAndPassword,
  getAuth,
  GithubAuthProvider,
  GoogleAuthProvider,
  onAuthStateChanged,
  RecaptchaVerifier,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signInWithPopup,
  signOut,
  TwitterAuthProvider,
} from 'firebase/auth';
import { collection, doc, getDoc, getFirestore, setDoc } from 'firebase/firestore';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useReducer } from 'react';
// config
import { FIREBASE_API } from 'src/config-global';
//
import userService from 'src/services/user-service';
import { setTokenGetter } from 'src/utils/axios-visa';
import { AuthContext } from './auth-context';

// ----------------------------------------------------------------------

const firebaseApp = initializeApp(FIREBASE_API);

export const AUTH = getAuth(firebaseApp);
AUTH.settings.appVerificationDisabledForTesting = false;

export const DB = getFirestore(firebaseApp);

// ----------------------------------------------------------------------

const initialState = {
  user: null,
  loading: true,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(() => {
    try {
      // Set up the token getter for axios
      setTokenGetter(async () => {
        const { currentUser } = AUTH;
        if (currentUser) {
          return currentUser.getIdToken();
        }
        return null;
      });

      onAuthStateChanged(AUTH, async (user) => {
        console.log('onAuthStateChanged', user);
        let profile = {
          displayName: null,
          email: null,
        };

        if (user) {
          const resp = await userService.getUser(user.uid);
          console.log('response', resp);
          if (resp.displayName && resp.email) {
            profile = resp;
          }

          dispatch({
            type: 'INITIAL',
            payload: {
              user: {
                ...user,
                ...profile,
                id: user.uid,
              },
            },
          });
        } else {
          dispatch({
            type: 'INITIAL',
            payload: {
              user: null,
            },
          });
        }
      });
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email, password) => {
    await signInWithEmailAndPassword(AUTH, email, password);
  }, []);

  const loginWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();

    await signInWithPopup(AUTH, provider);
  }, []);
  const loginWithGithub = useCallback(async () => {
    const provider = new GithubAuthProvider();

    await signInWithPopup(AUTH, provider);
  }, []);

  const loginWithTwitter = useCallback(async () => {
    const provider = new TwitterAuthProvider();

    await signInWithPopup(AUTH, provider);
  }, []);

  const setUpRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(AUTH, 'recaptcha-container', {
        size: 'invisible', // Use 'invisible' to automatically verify, or 'normal' for visible
        callback: (response) => {
          console.log('reCAPTCHA resolved with response:', response);
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired, please solve it again');
        },
      });
    }
  };

  const loginWithPhoneNumber = useCallback(async (phoneNumber, setError, setLoading) => {
    setUpRecaptcha();

    const appVerifier = window.recaptchaVerifier;
    console.log('appVerifier', appVerifier);
    setLoading(true);

    try {
      const confirmationResult = await signInWithPhoneNumber(AUTH, phoneNumber, appVerifier);
      console.log('confirmationResult', confirmationResult);
      window.confirmationResult = confirmationResult;
      console.log('SMS sent successfully');
      setLoading(false);
    } catch (error) {
      console.error('Error during sign-in with phone number', error);
      setError('Failed to send OTP. Please try again.');
      setLoading(false);
      throw error;
    }
  }, []);

  const register = useCallback(async (email, password, firstName, lastName) => {
    const newUser = await createUserWithEmailAndPassword(AUTH, email, password);

    await sendEmailVerification(newUser.user);

    const userProfile = doc(collection(DB, 'users'), newUser.user?.uid);

    await setDoc(userProfile, {
      uid: newUser.user?.uid,
      email,
      displayName: `${firstName} ${lastName}`,
    });

    const docSnap = await getDoc(userProfile);
    const profile = docSnap.data();

    console.log('profile', profile);

    dispatch({
      type: 'INITIAL',
      payload: {
        user: {
          ...newUser.user,
          ...profile,
          id: newUser.user.uid,
        },
      },
    });
  }, []);

  const logout = useCallback(async () => {
    await signOut(AUTH);
  }, []);

  const forgotPassword = useCallback(async (email) => {
    await sendPasswordResetEmail(AUTH, email);
  }, []);

  const refreshProfile = useCallback(
    async (user = state.user) => {
      dispatch({
        type: 'INITIAL',
        payload: {
          user: {
            ...state.user,
            loading: true,
          },
        },
      });

      try {
        const response = await userService.getUser(user.uid);

        if (response) {
          dispatch({
            type: 'INITIAL',
            payload: {
              user: {
                ...user,
                ...response,
                id: user.uid,
                loading: false,
              },
            },
          });
        } else {
          dispatch({
            type: 'INITIAL',
            payload: {
              user: {
                ...user,
                loading: false,
              },
            },
          });
        }
      } catch (error) {
        console.error('Error refreshing profile:', error);
        dispatch({
          type: 'INITIAL',
          payload: {
            user: {
              ...user,
              loading: false,
            },
          },
        });
      }
    },
    [state.user]
  );

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'firebase',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      logout,
      register,
      forgotPassword,
      loginWithGoogle,
      loginWithGithub,
      loginWithTwitter,
      loginWithPhoneNumber,
      refreshProfile,
    }),
    [
      status,
      state.user,
      //
      login,
      logout,
      register,
      forgotPassword,
      loginWithGithub,
      loginWithGoogle,
      loginWithTwitter,
      loginWithPhoneNumber,
      refreshProfile,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
